import { Link } from 'react-router-dom';
import cn from 'classnames';
import { Button, EditableText, Skeleton, lucideIcons, toast } from '@round/creatorbase-ui';
import { outreach } from '@round/api';
import { OutreachStatusBadge } from '../../../components/OutreachStatusBadge/OutreachStatusBadge';
import { useUpdateOutreachRequestName } from '../../hooks/useUpdateOutreachRequestName';
import { BACK_TO_LIST_LABEL, OUTREACH_LIST_PATH } from '../../helpers';
import { CancelOutreachRequestButton } from './components/CancelOutreachRequestButton/CancelOutreachRequestButton';
import { getMetaItems } from './helpers';
import styles from './OutreachRequestInfo.module.css';

const { ArrowLeft } = lucideIcons;

const NAME_PLACEHOLDER = 'Untitled request';
const NAME_LABEL = 'Request name';

/** Offered while the request is still loading too, so the headline doesn't gain a control as it settles. */
const BackToListButton = () => (
    <Button asChild variant="ghost" size="icon-sm" className={styles.backButton} aria-label={BACK_TO_LIST_LABEL}>
        <Link to={OUTREACH_LIST_PATH}>
            <ArrowLeft />
        </Link>
    </Button>
);

type Props = {
    request: outreach.OutreachRequest | undefined;
    isLoading: boolean;
};

export const OutreachRequestInfo = ({ request, isLoading }: Props) => {
    const { updateName } = useUpdateOutreachRequestName(request?.id);

    const handleNameCommit = (name: string) => {
        if (!request) {
            return;
        }

        // A blank name is a 400 too; catching it here saves a round trip that could only fail.
        if (name.trim() === '') {
            toast.error("Request name can't be empty");
            return;
        }

        updateName({ id: request.id, name });
    };

    if (isLoading) {
        return (
            <div className={styles.info}>
                <div className={styles.nameRow}>
                    <BackToListButton />
                    <Skeleton className={styles.nameSkeleton} />
                </div>
                <Skeleton className={styles.metaSkeleton} />
            </div>
        );
    }

    // A request that could not be read is reported by the page, in place of this headline.
    if (!request) {
        return null;
    }

    return (
        <div className={styles.info}>
            <div className={styles.nameRow}>
                <BackToListButton />
                <h1 className={styles.name}>
                    <EditableText
                        value={request.name}
                        onChange={handleNameCommit}
                        placeholder={NAME_PLACEHOLDER}
                        ariaLabel={NAME_LABEL}
                        displayClassName={styles.nameText}
                        editClassName={cn(styles.nameText, styles.nameEditField)}
                    />
                </h1>
                <OutreachStatusBadge status={request.status} />
                <CancelOutreachRequestButton request={request} className={styles.cancelButton} />
            </div>

            <dl className={styles.meta}>
                {getMetaItems(request).map(({ label, value }) => (
                    <div key={label} className={styles.metaItem}>
                        <dt className={styles.metaLabel}>{label}</dt>
                        <dd className={styles.metaValue}>{value}</dd>
                    </div>
                ))}
            </dl>
        </div>
    );
};
